import React, { RefObject } from 'react';
import { Recipe } from '../../api/Recipe';
import EditRecipeActionBar from '../../domains/recipes/components/EditRecipeActionBar';
import EditRecipePhotoStage from '../../domains/recipes/components/EditRecipePhotoStage';
import { Avatar, Box, Button, Card, CardBody, CardSubtitle, Flex } from '../ui';
import RecipeTextSections from './RecipeTextSections';
import RecipeTitleBlock from './RecipeTitleBlock';

interface EditRecipeReadViewProps {
  recipe: Recipe;
  isAuthor: boolean;
  isRecipeFavorite: boolean;
  favoritePendingIds: string[];
  isStickyRecipeHeaderVisible: boolean;
  stickyHeaderProgress: number;
  isSharedPhotoVisible: boolean;
  sharedPhotoStyle: React.CSSProperties | null;
  isImageOverlayOpen: boolean;
  imageWrapperRef: RefObject<HTMLDivElement | null>;
  tapedPhotoEffectRef: RefObject<HTMLDivElement | null>;
  stickyImageButtonRef: RefObject<HTMLButtonElement | null>;
  onOpenImageOverlay: () => void;
  onCloseImageOverlay: () => void;
  onFavoriteToggle: () => void;
  onShare: () => void;
  onOpenFullScreen: () => void;
  onToggleEdit: () => void;
}

const getRecipeImageSrc = (recipe: Recipe) =>
  recipe.image || `${process.env.PUBLIC_URL}/marmite.png`;

const EditRecipeReadView: React.FC<EditRecipeReadViewProps> = ({
  recipe,
  isAuthor,
  isRecipeFavorite,
  favoritePendingIds,
  isStickyRecipeHeaderVisible,
  stickyHeaderProgress,
  isSharedPhotoVisible,
  sharedPhotoStyle,
  isImageOverlayOpen,
  imageWrapperRef,
  tapedPhotoEffectRef,
  stickyImageButtonRef,
  onOpenImageOverlay,
  onCloseImageOverlay,
  onFavoriteToggle,
  onShare,
  onOpenFullScreen,
  onToggleEdit,
}) => {
  const recipeImageSrc = getRecipeImageSrc(recipe);

  return (
    <Card className="printable-content" shadow="sm" marginBottom={4}>
      <EditRecipePhotoStage
        recipe={recipe}
        recipeImageSrc={recipeImageSrc}
        isStickyRecipeHeaderVisible={isStickyRecipeHeaderVisible}
        stickyHeaderProgress={stickyHeaderProgress}
        isSharedPhotoVisible={isSharedPhotoVisible}
        sharedPhotoStyle={sharedPhotoStyle}
        isImageOverlayOpen={isImageOverlayOpen}
        imageWrapperRef={imageWrapperRef}
        tapedPhotoEffectRef={tapedPhotoEffectRef}
        stickyImageButtonRef={stickyImageButtonRef}
        onOpenImageOverlay={onOpenImageOverlay}
        onCloseImageOverlay={onCloseImageOverlay}
      />
      <CardBody>
        <EditRecipeActionBar
          recipe={recipe}
          isRecipeFavorite={isRecipeFavorite}
          favoritePendingIds={favoritePendingIds}
          onFavoriteToggle={onFavoriteToggle}
          onShare={onShare}
          onOpenFullScreen={onOpenFullScreen}
        />

        <Box id="recipe-print-area">
          <RecipeTitleBlock
            recipe={recipe}
            cardTitle
            titleWrapperFlex
            titleWrapperAlign="center"
            titleWrapperJustify="between"
            titleWrapperLayout={{ marginBottom: 3 }}
            titleLayout={{ marginBottom: 0, marginEnd: 3 }}
            tagsContainerJustify="center"
            tagClassName="recipe-tag-badge"
            renderAuthor={(recipeWithAuthor) => (
              recipeWithAuthor.authors ? (
                <Flex align="center" background="light" rounded="pill" shadow="sm" padding={2}>
                  {recipeWithAuthor.authors?.avatar_url && (
                    <Avatar
                      src={recipeWithAuthor.authors.avatar_url}
                      alt={`Avatar de ${recipeWithAuthor.authors.first_name}`}
                      marginEnd={2}
                      size={30}
                      style={{
                        border: `2px solid ${recipeWithAuthor.authors.color || 'transparent'}`,
                      }}
                    />
                  )}
                  {recipeWithAuthor.authors && (
                    <CardSubtitle as="p" textColor="dark" marginBottom={0} style={{ color: '#343a40' }}>
                      Par {recipeWithAuthor.authors.first_name} {recipeWithAuthor.authors.name}
                    </CardSubtitle>
                  )}
                </Flex>
              ) : null
            )}
          />
          <RecipeTextSections
            recipe={recipe}
            ingredientsHeading="Ingrédients :"
            stepsHeading="Étapes :"
          />
        </Box>

        {isAuthor && (
          <Button className="no-print" marginTop={3} onClick={onToggleEdit} data-testid="edit-recipe-button">
            Modifier la recette
          </Button>
        )}
      </CardBody>
    </Card>
  );
};

export default EditRecipeReadView;
